import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classnamesBind from 'classnames/bind';
import NavMenu from '@concur/nui-widgets/NavMenu';
import Icon from '@concur/nui-widgets/Icon';
import keycode from 'keycode';
import chain from 'chain-function';
import { withFormatter } from '@concur/nui-intl-runtime';
import { compose } from '@concur/core-ui-shell';
import { getSideNavIcon } from '../utils/sideNavIconMap';
import styles from './SideNav.css';

const CSS_BLOCK = 'sapcnqr-sidenav';
const CSS_PREFIX = 'sapcnqr';

const SideNavItemMenu = React.forwardRef((props, ref) => {
    const {
        buttonProps,
        children,
        className,
        item,
        formatter,
        onHide,
        overlayProps,
        ...otherProps
    } = props;

    const classnames = classnamesBind.bind(styles);
    const [isHovered, setIsHovered] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const [isOpen, setIsOpen] = useState(false);

    // prevent adding hovered styles when the submenu popover is open
    const setHoverState = useCallback((state) => {
        if (isOpen) {
            setIsHovered(false);
        } else {
            setIsHovered(state);
        }
    }, [isOpen]);

    useEffect(() => {
        if (isOpen) {
            setIsHovered(false);
        }
    }, [isOpen]);

    return (
        <NavMenu
            {...otherProps}
            className={classnames(
                className,
                { [`${CSS_BLOCK}__list-item--hovered`]: isHovered },
            )}
            inToolbarOverflow
            listProps={{
                className: classnames(CSS_PREFIX, `${CSS_BLOCK}__container`),
            }}
            overlayProps={{
                ...overlayProps,
                className: classnames(CSS_PREFIX, `${CSS_BLOCK}__overlay`, overlayProps?.className),
            }}
            ref={ref}
            buttonContents={isHovered ? (
                <>
                    <Icon iconName={getSideNavIcon(item.id)} ariaHidden />
                    {item.name}
                </>

            ) : (
                <Icon
                    iconName={getSideNavIcon(item.id)}
                    ariaLabel={item.name}
                />
            )}
            buttonProps={{
                ...buttonProps,
                className: classnames(
                    buttonProps?.className,
                    CSS_PREFIX,
                    {
                        // only add hovered styles if the item is not in overflow menu
                        [`${CSS_BLOCK}__list-item--hovered`]: isHovered,
                        [`${CSS_BLOCK}__list-item--open`]: isOpen,
                    },
                ),
                onMouseEnter: () => setHoverState(true),
                onMouseLeave: () => {
                // do not remove hover styles if the item is focused
                    if (!isFocused && !isOpen) {
                        setHoverState(false);
                    }
                },
                onFocus: () => {
                    setHoverState(true);
                    setIsFocused(true);
                },
                onBlur: () => {
                    setHoverState(false);
                    setIsFocused(false);
                },
                onKeyDown: (event) => {
                    if (keycode(event) === 'right') {
                        event.preventDefault();
                        setIsOpen(true);
                    }
                },
            }}
            onShow={() => setIsOpen(true)}
            onHide={chain(() => setIsOpen(false), onHide)}
            show={isOpen}
        >
            {children}
        </NavMenu>
    );
});

SideNavItemMenu.displayName = 'SideNavItemMenu';

SideNavItemMenu.propTypes = {
    className: PropTypes.string,
    buttonProps: PropTypes.object,
    item: PropTypes.object,
    overlayProps: PropTypes.object,
    children: PropTypes.node,
    onHide: PropTypes.func,
};

export default compose(
    withFormatter,
)(SideNavItemMenu);
